/**
 * categoriesService.ts — Cliente del catálogo bilingüe de categorías
 * GMB del backend MapeoData.
 *
 * **Backend canónico**: `/api/v1/categories/search` + `/api/v1/categories/:id`
 * (DEC-010 versionado). Servido desde `backend/src/data/gmb_categories_catalog.json`
 * (90 entradas oficiales GMB con label_es/en + aliases_es/en + sector +
 * country_relevance). DEC-018: catálogo local (no provider externo), DEC-016:
 * datos empíricos, nunca invención.
 *
 * **Decisión arquitectónica T-011** (2026-05-12 sesión 3): NO embeddings
 * vectoriales. Búsqueda textual sobre 90 entradas estáticas con aliases
 * + sectores cubre el caso de Hito 1 dogfooding. KISS YAGNI — embeddings
 * + pgvector serían over-engineering para esta cardinalidad. Si en el
 * futuro el catálogo crece a miles o se requiere búsqueda semántica
 * compleja, se reevalúa con DEC nueva.
 *
 * **Fallback mock built-in** (paridad con Metriclave frontend, decisión I
 * heredada T-CATALOG-FRONT): si el endpoint backend cae o devuelve 5xx,
 * el service responde con catálogo mock mínimo (10 entradas core ES) +
 * marca `is_fallback: true` para que la UI muestre banner discreto. La
 * app sigue siendo usable incluso con backend caído.
 *
 * **Cache de lookups individuales**: el componente `CategoryAutocomplete`
 * gestiona su propio cache interno por `(id, lang)`. Este service NO
 * cachea — solo es transporte (cada `getCategory` y `searchCategories`
 * es round-trip al backend).
 *
 * @module services/categoriesService
 */

import { api, ApiException } from '@/lib/api'

// ============================================================================
// TIPOS CANÓNICOS — paridad con backend `categories_service.ts`
// ============================================================================

/** Idiomas soportados por el catálogo (ES/EN). */
export type CategoryLang = 'es' | 'en'

/**
 * Item de categoría devuelto por `searchCategories` y `getCategory`.
 *
 * Campos canónicos del backend:
 *   - `id`: category_id oficial GMB en snake_case en inglés. Inviolable.
 *   - `label`: ya resuelto al idioma pedido (es/en). No incluye ambos.
 *   - `aliases`: ya resuelto al idioma pedido.
 *   - `sector`: clave de sector en inglés (e.g. `professional_services_legal`).
 */
export interface CategoryItem {
  id: string
  label: string
  aliases: string[]
  sector: string
}

/**
 * Respuesta enriquecida de `searchCategories`. El backend devuelve
 * `{success, data}` pero aquí lo aplanamos a `{data, is_fallback}` para
 * que el componente sepa si los resultados vienen del backend real o
 * del mock built-in.
 */
export interface SearchCategoriesResponse {
  data: CategoryItem[]
  /** True si los resultados vienen del mock fallback (backend caído). */
  is_fallback: boolean
}

// ============================================================================
// ERROR TIPADO
// ============================================================================

export type CategoriesErrorCode =
  | 'auth'
  | 'validation'
  | 'not_found'
  | 'server'
  | 'network'
  | 'aborted'

export class CategoriesError extends Error {
  public readonly code: CategoriesErrorCode

  constructor(code: CategoriesErrorCode, message: string) {
    super(message)
    this.name = 'CategoriesError'
    this.code = code
  }
}

// ============================================================================
// MOCK FALLBACK (10 entradas core ES — solo si backend cae)
// ============================================================================

/**
 * Catálogo mock mínimo en español. Activado por `searchCategories` cuando
 * el backend devuelve 5xx o network error. Las 10 entradas son las más
 * comunes en el mercado foco España (sectores variados para que el
 * autocomplete sea útil aunque sea limitado).
 *
 * Mantener IDs y sectores idénticos al backend para que la UI no se rompa
 * al alternar entre fallback y real. Si añades una entrada al backend
 * que sea muy común, considera replicarla aquí.
 */
const MOCK_CATEGORIES_ES: CategoryItem[] = [
  { id: 'lawyer', label: 'Abogado', aliases: ['abogada', 'bufete', 'letrado'], sector: 'professional_services_legal' },
  { id: 'beauty_salon', label: 'Salón de belleza', aliases: ['estética', 'centro de belleza'], sector: 'beauty_personal_care' },
  { id: 'car_dealer', label: 'Concesionario de coches', aliases: ['concesionario', 'venta de coches'], sector: 'automotive' },
  { id: 'restaurant', label: 'Restaurante', aliases: ['restaurantes', 'comida'], sector: 'food_beverage' },
  { id: 'dentist', label: 'Dentista', aliases: ['clínica dental', 'odontólogo'], sector: 'health_medical' },
  { id: 'electrician', label: 'Electricista', aliases: ['instalación eléctrica'], sector: 'construction_trades' },
  { id: 'plumber', label: 'Fontanero', aliases: ['fontanería', 'plomero'], sector: 'construction_trades' },
  { id: 'driving_school', label: 'Autoescuela', aliases: ['carnet de conducir'], sector: 'education' },
  { id: 'real_estate_agency', label: 'Inmobiliaria', aliases: ['agencia inmobiliaria'], sector: 'real_estate' },
  { id: 'hair_salon', label: 'Peluquería', aliases: ['barbería'], sector: 'beauty_personal_care' },
]

const MOCK_CATEGORIES_EN: CategoryItem[] = [
  { id: 'lawyer', label: 'Lawyer', aliases: ['attorney', 'law firm'], sector: 'professional_services_legal' },
  { id: 'beauty_salon', label: 'Beauty Salon', aliases: ['salon'], sector: 'beauty_personal_care' },
  { id: 'car_dealer', label: 'Car Dealer', aliases: ['car dealership'], sector: 'automotive' },
  { id: 'restaurant', label: 'Restaurant', aliases: ['restaurants'], sector: 'food_beverage' },
  { id: 'dentist', label: 'Dentist', aliases: ['dental clinic'], sector: 'health_medical' },
  { id: 'electrician', label: 'Electrician', aliases: [], sector: 'construction_trades' },
  { id: 'plumber', label: 'Plumber', aliases: [], sector: 'construction_trades' },
  { id: 'driving_school', label: 'Driving School', aliases: [], sector: 'education' },
  { id: 'real_estate_agency', label: 'Real Estate Agency', aliases: [], sector: 'real_estate' },
  { id: 'hair_salon', label: 'Hair Salon', aliases: ['barbershop'], sector: 'beauty_personal_care' },
]

function filterMock(catalog: CategoryItem[], q: string, limit: number): CategoryItem[] {
  const norm = q.trim().toLowerCase()
  if (!norm) return catalog.slice(0, limit)
  return catalog
    .filter(c => {
      if (c.label.toLowerCase().includes(norm)) return true
      return c.aliases.some(a => a.toLowerCase().includes(norm))
    })
    .slice(0, limit)
}

// ============================================================================
// API: searchCategories
// ============================================================================

export interface SearchCategoriesParams {
  /** Query libre. Vacío permitido (devuelve top). */
  q: string
  /** Idioma resultado. Default 'es'. */
  lang?: CategoryLang
  /** Filtro por país ISO-2 (e.g. 'ES'). */
  country?: string
  /** Default 20. Backend valida 1..50. */
  limit?: number
  /** AbortSignal del componente. */
  signal?: AbortSignal
}

/**
 * `GET /api/v1/categories/search?q=...&lang=es&country=ES&limit=20`
 *
 * Si el backend falla (5xx, network, timeout), devuelve mock fallback
 * con `is_fallback: true`. Si el backend devuelve 401/403 o 4xx
 * de validación, lanza `CategoriesError` (no se enmascara — el usuario
 * debe ver el problema real).
 */
export async function searchCategories(
  params: SearchCategoriesParams,
): Promise<SearchCategoriesResponse> {
  const lang = params.lang ?? 'es'
  const limit = params.limit ?? 20

  try {
    const resp = await api<{ success: boolean; data: CategoryItem[] }>(
      '/categories/search',
      {
        method: 'GET',
        query: {
          q: params.q,
          lang,
          country: params.country,
          limit,
        },
        signal: params.signal,
      },
    )
    return {
      data: resp.data ?? [],
      is_fallback: false,
    }
  } catch (e) {
    // Errors de auth o validación: propagar para que UI muestre adecuado.
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new CategoriesError('aborted', 'Request cancelado')
    }
    if (e instanceof ApiException) {
      if (e.status === 401 || e.status === 403) {
        throw new CategoriesError('auth', 'Sesión expirada o sin permisos')
      }
      if (e.status === 400 || e.status === 422) {
        throw new CategoriesError(
          'validation',
          e.apiError?.message ?? 'Parámetros de búsqueda inválidos',
        )
      }
      // 5xx → cae al mock fallback (no lanzar error).
    }
    // Network error (TypeError) o 5xx → fallback mock.
    const catalog = lang === 'en' ? MOCK_CATEGORIES_EN : MOCK_CATEGORIES_ES
    return {
      data: filterMock(catalog, params.q, limit),
      is_fallback: true,
    }
  }
}

// ============================================================================
// API: getCategory
// ============================================================================

/**
 * Respuesta enriquecida de `getCategory` — `data: null` cuando el id
 * no existe (sin lanzar excepción), para que el consumidor pueda
 * decidir mostrar placeholder en lugar de error UI. Patrón heredado
 * Metriclave T-CATALOG-FRONT — el autocomplete prefiere "no romper"
 * cuando un id antiguo desaparece del catálogo a romper la UI entera.
 */
export interface GetCategoryResponse {
  data: CategoryItem | null
  is_fallback: boolean
}

/**
 * `GET /api/v1/categories/:id?lang=es`
 *
 * Resuelve un `category_id` a su detalle completo en el idioma indicado.
 * Usado por `CategoryAutocomplete` para resolver labels de ids que ya
 * vienen en `value` (e.g. recuperados de una búsqueda guardada o de
 * filtros persistidos en URL).
 *
 * **Política "no romper UI"**:
 *   - id existe en backend → `{data: CategoryItem, is_fallback: false}`.
 *   - id NO existe en backend (404) → `{data: null, is_fallback: false}`.
 *   - Backend caído + id en mock → `{data: CategoryItem, is_fallback: true}`.
 *   - Backend caído + id NO en mock → `{data: null, is_fallback: true}`.
 *   - Auth error (401/403) → lanza `CategoriesError('auth')` (no se enmascara).
 *   - Aborted (signal) → lanza `CategoriesError('aborted')`.
 */
export async function getCategory(
  id: string,
  lang: CategoryLang = 'es',
  signal?: AbortSignal,
): Promise<GetCategoryResponse> {
  try {
    const resp = await api<{ success: boolean; data: CategoryItem & { country_relevance?: string[] } }>(
      `/categories/${encodeURIComponent(id)}`,
      {
        method: 'GET',
        query: { lang },
        signal,
      },
    )
    // El detail devuelve también country_relevance, pero CategoryItem no lo
    // expone (no lo necesita el autocomplete). Filtrar shape.
    return {
      data: {
        id: resp.data.id,
        label: resp.data.label,
        aliases: resp.data.aliases,
        sector: resp.data.sector,
      },
      is_fallback: false,
    }
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new CategoriesError('aborted', 'Request cancelado')
    }
    if (e instanceof ApiException) {
      if (e.status === 404) {
        // id desconocido — devolver null silenciosamente (política no romper UI).
        return { data: null, is_fallback: false }
      }
      if (e.status === 401 || e.status === 403) {
        throw new CategoriesError('auth', 'Sesión expirada o sin permisos')
      }
    }
    // Fallback mock: búsqueda exacta por id.
    const catalog = lang === 'en' ? MOCK_CATEGORIES_EN : MOCK_CATEGORIES_ES
    const found = catalog.find(c => c.id === id)
    return {
      data: found ?? null,
      is_fallback: true,
    }
  }
}
